import { computed, inject, provide, unref, type InjectionKey, type MaybeRef, type Ref } from 'vue';

/**
 * cas-c860 (Ben 2026-10-21 16:49Z, wording approved 19:03Z): an inspiration post is not graded,
 * so the audit/grade wording on its page reads as insights ("overall audit" → "overall insights",
 * Spanish "claves"). Each approved string has an inspiration variant at
 * `inspirationInsights.<original key>`; own audited posts keep the original key and value.
 */
export const INSPIRATION_INSIGHTS_PREFIX = 'inspirationInsights';

/** The original keys with an approved inspiration variant (WORDING-INVENTORY.md, cas-c860). */
export const INSPIRATION_INSIGHTS_KEYS: ReadonlySet<string> = new Set([
  'postAuditV3.categoryAudit.overall',
  'postAuditV3.deepDive.cardBody',
  'postAuditV3.deepDive.subtitle',
  'postAuditV3.deepDive.gradeDetails',
  'postAuditV3.postContent.empty',
  'admin.qstashRapidApiPoc.postInspector.headerCard.auditedUnknown',
  'admin.qstashRapidApiPoc.postInspector.headerCard.auditedJustNow',
  'admin.qstashRapidApiPoc.postInspector.headerCard.auditedMinutesAgo',
  'admin.qstashRapidApiPoc.postInspector.headerCard.auditedHoursAgo',
  'admin.qstashRapidApiPoc.postInspector.headerCard.auditedDaysAgo',
  'admin.qstashRapidApiPoc.postInspector.headerCard.auditedOnDate',
  'admin.qstashRapidApiPoc.postInspector.carousel.sections.analysisGradesSubtitle',
  'admin.qstashRapidApiPoc.postInspector.carousel.sections.knowledgeSourcesSubtitle',
  'admin.qstashRapidApiPoc.postInspector.carousel.processingSteps.preparingAudit',
  'admin.qstashRapidApiPoc.postInspector.carousel.noData',
  'postAuditV2.progress.label',
  'postAuditV2.progress.ready',
  'postAuditV2.steps.buildingAudit',
  'postAuditV2.failure.title',
  'postAuditV2.failure.reprocessFailed',
  'postAudit.errors.inspirationAnalysisUnavailable',
  'postAudit.errors.checkoutLaunchFailed',
  'auditUnlock.offer.title',
  'auditUnlock.phone.subtitle',
  'auditUnlock.phone.inboundOnly',
  'auditUnlock.phone.notFound',
  'auditUnlock.success.title',
  'admin.viralInspiration.auditCta.subtitle'
]);

/** The key to read: the inspiration variant on an inspiration post, else the original. */
export function inspirationInsightsKey(
  key: string,
  isInspiration: boolean | null | undefined
): string {
  return isInspiration === true && INSPIRATION_INSIGHTS_KEYS.has(key)
    ? `${INSPIRATION_INSIGHTS_PREFIX}.${key}`
    : key;
}

const INSPIRATION_POST_CONTEXT: InjectionKey<Ref<boolean>> = Symbol('inspiration-post-context');

/** The post page says whether the post on screen is an inspiration post. */
export function provideInspirationPostContext(isInspiration: MaybeRef<boolean>): void {
  provide(
    INSPIRATION_POST_CONTEXT,
    computed(() => unref(isInspiration) === true)
  );
}

/**
 * Components on the post page pick their key through this. Outside an inspiration post (no
 * provider, or an own audit) every key is returned unchanged.
 */
export function useInspirationInsightsKey(): (key: string) => string {
  const context = inject(INSPIRATION_POST_CONTEXT, null);
  return (key: string) => inspirationInsightsKey(key, context?.value === true);
}
